import { createApi } from '@reduxjs/toolkit/query/react'
import { customFetchBaseQuery } from '../app/baseQuery'
import Personnummer from 'personnummer'
import type {
  CreateBookingRequest,
  CreateBookingResponse,
  GetBookingFlowTypeRequest,
  GetBookingFlowTypeResponse,
  ProceedBookingRequest,
  ProceedBookingResponse,
} from '../../types/patient-api-gateway/api/patient/v1/patient_gateway_service'
import type {
  RescheduleExternalBookingRequest,
  RescheduleExternalBookingResponse,
} from '../../types/patient-api-gateway/api/patient/restricted/v1/restricted_gateway_service'
import { parsePhoneNumberFromString as parsePhoneNumber } from 'libphonenumber-js'

export type CreateBookingMutationArgs = {
  clinicId: string
  appointmentId: string
  fName: string
  lName: string
  email: string
  phone: string
  ssn: string
  message?: string
  promoCode?: string
  idToken?: string
}

export const bookingFlowApi = createApi({
  reducerPath: 'bookingFlowApi',
  baseQuery: customFetchBaseQuery,
  tagTypes: ['ExternalBooking'],
  endpoints: (builder) => ({
    createBooking: builder.mutation<
      CreateBookingResponse,
      CreateBookingMutationArgs
    >({
      query: ({
        clinicId,
        appointmentId,
        fName,
        lName,
        email,
        phone,
        ssn,
        message,
        promoCode,
        idToken,
      }) => ({
        url: '/api.patient.v1.PatientGatewayService/CreateBooking',
        method: 'POST',
        body: {
          appointmentClaim: {
            clinicId,
            appointmentId,
          },
          patient: {
            swedishPersonalIdentityNumber: {
              twelveDigitNumber: Personnummer.parse(ssn).format(true),
            },
            firstName: fName,
            lastName: lName,
            mobilePhone: {
              e164Number: parsePhoneNumber(phone, 'SE')?.number,
            },
            email,
          },
          promoCode,
          message,
        } satisfies CreateBookingRequest,
        ...(idToken && {
          headers: {
            'X-User-Token': idToken,
          },
        }),
      }),
    }),
    createProceedBooking: builder.mutation<
      ProceedBookingResponse,
      ProceedBookingRequest
    >({
      query: (body) => ({
        url: '/api.patient.v1.PatientGatewayService/ProceedBooking',
        method: 'POST',
        body: {
          ...body,
        } satisfies ProceedBookingRequest,
      }),
    }),
    getBookingFlowType: builder.query<
      GetBookingFlowTypeResponse,
      GetBookingFlowTypeRequest
    >({
      query: (body) => ({
        url: '/api.patient.v1.PatientGatewayService/GetBookingFlowType',
        method: 'POST',
        body: {
          ...body,
        } satisfies GetBookingFlowTypeRequest,
      }),
    }),
    rescheduleExternalBooking: builder.mutation<
      RescheduleExternalBookingResponse,
      RescheduleExternalBookingRequest & { token: string }
    >({
      query: ({ token, ...body }) => ({
        url: '/api.patient.restricted.v1.RestrictedGatewayService/RescheduleExternalBooking',
        method: 'POST',
        body: {
          ...body,
        } satisfies RescheduleExternalBookingRequest,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    }),
  }),
})

export const {
  useCreateBookingMutation,
  useCreateProceedBookingMutation,
  useGetBookingFlowTypeQuery,
  useRescheduleExternalBookingMutation,
} = bookingFlowApi
